import type { Recipe } from '@ctcells/contract'
import { useState } from 'react'
import { intlLocale, t } from '@ctcells/i18n'

import { Button } from '../../atoms/Button.tsx'
import { DataTable, type Column } from '../../atoms/DataTable.tsx'
import { Field } from '../../atoms/Field.tsx'
import { Switch } from '../../atoms/Switch.tsx'
import { TextInput } from '../../atoms/TextInput.tsx'
import type { ActionGate } from '../access.ts'
import { Card } from '../resourceParts.tsx'
import {
  centrifugeRounds,
  REAGENT_CHANNEL_LIMIT,
  sampleGroupFacts,
  type ReagentRow,
  type RecipeFormState,
} from './recipeForm.ts'
import { withRecipeTablePriority } from './recipeModel.ts'

import '../screens.css'
import '../resource.css'

/**
 * 레시피 절 카드 — 와이어프레임 `rcpCards()` 2046행. **레시피 상세**가 쓴다(`readOnly` 로 갈린다).
 * ⚠ 「새 레시피 › 값 직접 설정」은 2026-09-29 부터 이 카드를 쓰지 않는다 — 스키마 칸으로 본문을 짓는
 *   편집기(`RecipeEditor.tsx`)가 따로 섰다. 이 카드는 절 on/off 와 시약 줄만 쥐어 본문을 못 지었다.
 *
 * 🔴 **부제에서 스키마 필드명을 걷어냈다.** 와이어프레임 2054·2056행은
 *    `speed_rpm · duration_sec · 회차` 를 그대로 노출하는데 그게 §1-1 위반이다(README §19-6).
 *    절 제목만 남긴다.
 *
 * 🔴 **카드를 «조각»으로도 내보낸다**(2026-09-21). 상세 화면이 Opentrons 프로토콜 상세처럼
 *    탭으로 갈리면서(`docs/opentrons-alignment.md` §4-2) 카드들이 서로 다른 탭에 놓이기
 *    때문이다 — 「단계」 탭은 모듈·트레이 카드를, 「시약」 탭은 시약 카드를, 「개요」 탭은
 *    공통 카드를 쓴다. 조각을 복사하지 말고 이 파일에서 가져다 써라.
 *    ⚠ 조각 전부를 쌓던 묶음(`RecipeSections`)은 2026-09-29 걷었다 — 쓰던 곳(새 레시피)이 편집기로 갔다.
 */
export interface RecipeSectionsProps {
  state: RecipeFormState
  onChange?: ((next: RecipeFormState) => void) | undefined
  /** 편집 게이트. 비활성이면 사유가 입력마다 붙는다 */
  edit: ActionGate
  /**
   * 🔴 **고칠 수 있는 종류가 아니다**(확정·보관 레시피). `edit` 게이트보다 앞선다.
   *
   * 종전에는 이 경우도 `edit` 비활성으로 흘려서 **값이 전부 흐린 글자**가 됐고, eric 이
   * 「시약 설정이 희미한데 이게 디폴트 값이야?」라고 물었다(2026-09-21). 그 값들은 전부
   * 그 레시피의 실제 값이었다. ⚠ 게다가 `gate()` 는 제어락을 상태보다 **먼저** 보기 때문에
   * 사유가 「제어락 필요」로 떴다 — 락을 쥐어도 고칠 수 없는 칸인데 **틀린 사유**였다.
   */
  readOnly?: boolean | undefined
  /** 값의 출처가 되는 레시피. 화면이 값을 지어내지 않는다 */
  recipe: Recipe
  /*
   * 🔴 **`speedWarning` 을 되살리지 마라**(2026-09-20 제거). 종전에는 검증 지적 중 「원심」이
   *    들어간 것을 **한글 문자열 대조**로 골라 이 필드에 붙였는데, 그 목록의 출처가 fixture
   *    (`RECIPE_CHECKS`)였다. 서버 `ValidationIssue` 는 `code`·`message`·`step_seq`·
   *    `field`(JSON Pointer)를 주고 **절 이름을 주지 않는다** — 붙일 자리를 추측할 수 없다.
   *    지적은 상세 화면의 검증 카드가 그대로 나열한다.
   */
}

/**
 * 값이 없으면 `—` 다. 🔴 **빈 칸으로 두지 않는다** — 읽기 전용 칸이 비어 있으면
 * 「값이 빈 문자열이다」와 「그 레시피가 말하지 않는다」가 구별되지 않는다.
 *
 * 🔴 **`—` 는 «값»이 아니라 placeholder 로 낸다**(2026-09-21). 종전에는 `value="—"` 였는데,
 *    그러면 진짜 값과 없는 값이 **같은 진하기**로 나란히 선다. placeholder 로 내면 브라우저가
 *    옅게 그려 주고, 무엇보다 **입력의 `value` 가 실제로 비어 있다** — 「지어낸 기본값이 없다」를
 *    시험으로 걸 때 `value === ''` 로 볼 수 있다(종전에는 `—` 라는 문자열을 대조해야 했다).
 */
function shown(value: string | undefined): { value: string; placeholder: string | undefined } {
  return value === undefined || value === ''
    ? { value: '', placeholder: t('mask.none') }
    : { value, placeholder: undefined }
}

/** 읽기 전용 표 칸의 글자 — 비었으면 `—`(지어낸 기본값이 없다 · `shown` 과 같은 규칙) */
function textCell(value: string) {
  return value === '' ? <span className="muted">{t('mask.none')}</span> : value
}

/**
 * 대표값 한 칸 — 🔴 **전부 같을 때만 그 값이다.** 갈리면 갈린 채로 보인다(`2 / 3`).
 * 「max 를 고른다」 같은 임의 규칙으로 하나를 뽑지 않는다 — 그것도 지어내는 것이다.
 */
function spread(values: readonly number[], unit = ''): string | undefined {
  if (values.length === 0) return undefined
  return values.map((v) => `${String(v)}${unit}`).join(' / ')
}

/**
 * 입력 한 칸이 어떤 꼴로 설 것인가 — 조각 카드들이 같은 규칙을 쓰도록 한 곳에서 만든다.
 *
 * 🔴 **읽기 전용이 게이트를 이긴다** — 고칠 수 없는 것이 「조건」이 아니라 「성질」이면
 *    비활성 사유를 달 것이 없다. 달면 락만 쥐면 고칠 수 있다는 거짓이 된다.
 */
type FieldMode = ActionGate | { readonly readOnly: true }

function modesOf(state: RecipeFormState, edit: ActionGate, readOnly: boolean) {
  /** 절이 꺼져 있으면 그 절의 입력은 비활성이다. 사유를 붙여야 하므로 게이트로 만든다 */
  const sectionGate = (on: boolean): ActionGate =>
    edit.disabled
      ? edit
      : on
        ? { disabled: false }
        : { disabled: true, disabledReason: t('reason.commandNotAllowed') }

  const mode = (g: ActionGate): FieldMode => (readOnly ? ({ readOnly: true } as const) : g)

  return {
    cent: mode(sectionGate(state.centrifuge)),
    asm: mode(sectionGate(state.assembler)),
    stain: mode(sectionGate(state.staining)),
    /** 절 단위가 아닌 칸(트레이 · 공통)과 토글이 쓴다 */
    editMode: mode(edit),
    /**
     * 시약 표의 칸은 «사람이 적은 값»이라 draft 에서는 그냥 값이다.
     * 읽기 전용일 때만 빈 칸을 `—`(placeholder)로 갈라 보인다 — 다른 칸과 같은 규칙.
     */
    cell: (value: string) => (readOnly ? shown(value) : { value }),
  }
}

/**
 * 원심 · 어셈블러 · 염색 세 절의 **파라미터 카드**.
 * ⚠ 시약 표는 여기 없다 — Opentrons 가 「액체」를 독립 탭으로 두듯 `RecipeReagentCard` 로 갈랐다.
 */
export function RecipeModuleCards({
  state,
  onChange,
  edit,
  readOnly = false,
  recipe,
}: RecipeSectionsProps) {
  const payload = recipe.payload
  const centrifuge = payload.centrifuge
  const assembler = payload.assembler
  const rounds = centrifugeRounds(payload)
  const {
    cent: centGate,
    asm: asmGate,
    stain: stainGate,
    editMode,
  } = modesOf(state, edit, readOnly)

  /**
   * 회차 표기 — 번호가 `1…N` 로 이어지면 **「N」**, 이가 빠졌으면 **번호를 그대로** 보인다.
   * 🔴 이어지지 않는 집합(`1 · 3`)을 「2」라고 말하지 않는다 — 회차 번호는 2가 아니라 3까지 간다.
   */
  const roundsText =
    rounds.length === 0
      ? undefined
      : rounds.every((round, index) => round === index + 1)
        ? String(rounds.length)
        : rounds.map(String).join(' · ')

  const toggle = (key: 'centrifuge' | 'assembler' | 'staining') => (checked: boolean) =>
    onChange?.({ ...state, [key]: checked })

  return (
    <>
      <Card
        title={t('section.centrifuge')}
        titleEnd={
          <Switch
            checked={state.centrifuge}
            label={t('section.centrifuge')}
            onChange={toggle('centrifuge')}
            {...editMode}
          />
        }
      >
        <div className="f2">
          <Field label={t('field.speed')}>
            <TextInput
              onChange={() => undefined}
              {...shown(
                centrifuge === undefined
                  ? undefined
                  : `${centrifuge.speed_rpm.toLocaleString(intlLocale())} rpm`,
              )}
              {...centGate}
            />
          </Field>
          <Field label={t('field.spinDuration')}>
            <TextInput
              onChange={() => undefined}
              {...shown(
                centrifuge === undefined ? undefined : `${String(centrifuge.duration_sec)} s`,
              )}
              {...centGate}
            />
          </Field>
          {/*
            🔴 **`"3"` 리터럴을 걷었다**(2026-09-21). 표준 레시피의 회차가 마침 3 이라
               **그 레시피에서만 맞아 보이고 다른 레시피에서 조용히 틀렸다.**
               출처는 `centrifuge.steps[].round` 하나다(`centrifugeRounds`).
          */}
          <Field label={t('field.rounds')}>
            <TextInput onChange={() => undefined} {...shown(roundsText)} {...centGate} />
          </Field>
        </div>
      </Card>

      <Card
        title={t('section.assembler')}
        titleEnd={
          <Switch
            checked={state.assembler}
            label={t('section.assembler')}
            onChange={toggle('assembler')}
            {...editMode}
          />
        }
      >
        <div className="f2">
          <Field label={t('field.uvDuration')}>
            <TextInput
              onChange={() => undefined}
              {...shown(
                assembler === undefined ? undefined : `${String(assembler.uv_duration_sec)} s`,
              )}
              {...asmGate}
            />
          </Field>
          <Field label={t('field.uvAngle')}>
            <TextInput
              onChange={() => undefined}
              {...shown(
                assembler?.uv_tilt_angle_deg === undefined
                  ? undefined
                  : `${String(assembler.uv_tilt_angle_deg)}°`,
              )}
              {...asmGate}
            />
          </Field>
          <Field label={t('field.loadTilt')}>
            <TextInput
              onChange={() => undefined}
              {...shown(
                assembler?.load_tilt_angle_deg === undefined
                  ? undefined
                  : `${String(assembler.load_tilt_angle_deg)}°`,
              )}
              {...asmGate}
            />
          </Field>
          <Field label={t('field.drainTilt')}>
            <TextInput
              onChange={() => undefined}
              {...shown(
                assembler === undefined ? undefined : `${String(assembler.drain_angle_deg)}°`,
              )}
              {...asmGate}
            />
          </Field>
        </div>
      </Card>

      <Card
        title={t('section.staining')}
        titleEnd={
          <Switch
            checked={state.staining}
            label={t('section.staining')}
            onChange={toggle('staining')}
            {...editMode}
          />
        }
      >
        {/*
          🔴 **「분주량」·「교반」 칸을 걷었다**(2026-09-21). 종전에는 `"1,000 µL"` ·
             `"350 rpm · 5 분"` 이 **리터럴로 박혀** 있어서 어느 레시피를 열어도 같은 수가
             떴다 — 이 카드의 다른 칸들과 같은 꼴이라 **레시피 값으로 읽힌다.**
             `recipe.schema.json` 의 `StainingParams` 에는 절 단위 분주량·교반이 **없다**
             (`default_tip_policy` · `default_liquid_class` · `reagent_storage_celsius` 뿐).
             `volume_ul` 과 `shake` 는 **스텝마다** 붙는 값이라 하나로 줄일 수 없다.
          ⚠ 되살리려면 스텝 표로 펼쳐야 한다. 대표값 한 칸으로 만들지 마라.

          🔴 **「워싱 횟수」도 같은 이유로 걷었다**(2026-09-21 · 종전 `value="5"`).
             **가장 나쁜 꼴이었다** — 실물 레시피에 `operation:"wash"` 스텝이 **0건**인데
             화면은 `5` 를 보였다. 빈 열이 아니라 **없는 사실을 말하는 칸**이었다.
             계약이 「워싱 횟수」로 부르는 값은 `StainingStep.repetitions`(「wash 반복 횟수」)
             **하나뿐이고 스텝마다 붙는다.** 절 단위 대표값이 `StainingParams` 에 없다
             (`recipe.schema.json` · `vocab/` 전수). 스텝 수를 세는 것은 「반복 횟수」가
             아니라 「워싱 스텝 수」라 **다른 값**이다 — 정의가 없으면 세지 않는다.
        */}
        <div className="f2">
          <Field label={t('field.storageTemp')}>
            <TextInput
              onChange={() => undefined}
              {...shown(
                payload.staining?.reagent_storage_celsius === undefined
                  ? undefined
                  : `${String(payload.staining.reagent_storage_celsius)} ℃`,
              )}
              {...stainGate}
            />
          </Field>
        </div>
      </Card>
    </>
  )
}

/**
 * 시약 카드 — Opentrons 프로토콜 상세의 **「액체(Liquids)」 탭**에 해당한다
 * (`docs/opentrons-alignment.md` §4-2). 우리 어휘로는 「시약」이다.
 *
 * 🔴 **「자리 · 웰 · 총 용량」 세 칸은 «입력이 아니라 값»이다**(2026-09-21). 셋 다
 *    `labware_id` 와 `volumeByWell`(웰 → µL 맵)에서 **파생**되는데, 화면에 그 맵을 고칠
 *    편집기가 없다 — 입력 상자로 두면 적을 수는 있는데 **되돌려 보낼 길이 없다.**
 *    (종전 「웰」 칸은 그보다 나빴다: 실물에 «없는» `well` 키를 읽어 **언제나 비어** 있었다.)
 * ⚠ 되살리려면 웰별 용량 편집기가 먼저 있어야 한다. 한 칸짜리 입력으로 만들지 마라.
 */
export function RecipeReagentCard({
  state,
  onChange,
  edit,
  readOnly = false,
}: Omit<RecipeSectionsProps, 'recipe'>) {
  const { stain: stainGate, cell } = modesOf(state, edit, readOnly)
  /* 행 펼침 — 서랍 1/3 에서 빠진 열(자리 · 웰 · 용량 · lot · 성분)이 서는 자리다(`RECIPE_TABLE_PRIORITY.reagents`) */
  const [openChannel, setOpenChannel] = useState<string | null>(null)
  const canAddChannel = state.reagents.length < REAGENT_CHANNEL_LIMIT

  const patchReagent = (channel: string, patch: Partial<ReagentRow>) =>
    onChange?.({
      ...state,
      reagents: state.reagents.map((r) => (r.channel === channel ? { ...r, ...patch } : r)),
    })

  const reagentColumns: Column<ReagentRow>[] = [
    {
      key: 'channel',
      header: t('column.channel'),
      nowrap: true,
      cell: (row) => <span className="mono">{row.channel}</span>,
    },
    {
      key: 'name',
      header: t('column.reagent'),
      /*
       * 🔴 **고칠 수 없는 레시피에서는 글자다**(2026-09-30 · 서랍 1/3). 읽기 전용 입력 상자는 좁은 서랍에서 값을
       *    상자 폭(44px)으로 잘라 보였다 — 「lot」 칸이 2026-09-22 에 이미 글자로 바뀐 것과 같은 결이다.
       *    초안 · 새 레시피에서는 입력 상자 그대로다.
       */
      cell: (row) =>
        readOnly ? (
          textCell(row.name)
        ) : (
          <TextInput
            aria-label={t('column.reagent')}
            onChange={(v) => patchReagent(row.channel, { name: v })}
            {...cell(row.name)}
            wide
            {...stainGate}
          />
        ),
    },
    {
      key: 'component',
      header: t('column.component'),
      cell: (row) =>
        readOnly ? (
          textCell(row.component)
        ) : (
          <TextInput
            aria-label={t('column.component')}
            onChange={(v) => patchReagent(row.channel, { component: v })}
            {...cell(row.component)}
            wide
            {...stainGate}
          />
        ),
    },
    {
      /*
       * 🔴 파생값이라 읽기다 — 카드 머리말 참조. `labware[].id` 를 가리킨다.
       * ⚠ **`nowrap` 을 붙이지 마라.** 실물 값이 `staining.reagent_bay.ambient.1` 처럼 길어서
       *   붙이면 표가 카드를 넘어 **오른쪽 요약칸을 덮는다**(2026-09-21 실화면에서 났다).
       * 🔴 `nowrap` 을 뗀 것만으로는 모자랐다 — 이 값에는 **공백이 없어서** 일반 줄바꿈이
       *    일어날 자리가 없고, 열의 min-content 가 값 전체 길이로 남는다. 실측으로 표가
       *    카드보다 73px 넓었다(1315 vs 1242). `wrapAnywhere` 로 글자 사이를 끊는다.
       */
      key: 'bay',
      header: t('column.bay'),
      wrapAnywhere: true,
      cell: (row) => (
        <span className="mono">{row.labwareId === '' ? t('mask.none') : row.labwareId}</span>
      ),
    },
    {
      /* ⚠ 여기는 `nowrap` 이다 — `A1 · A2 · A3` 가 가운뎃점마다 줄바꿈되면 행 높이가 값마다 달라진다 */
      key: 'well',
      header: t('column.well'),
      nowrap: true,
      cell: (row) => <span className="mono">{row.wells === '' ? t('mask.none') : row.wells}</span>,
    },
    {
      /* ⚠ `null` 은 「0 µL」이 아니라 «모른다»다 — `volumeByWell` 이 없는 채널이다 */
      key: 'volume',
      header: t('column.totalVolume'),
      nowrap: true,
      cell: (row) => (
        <span className="mono">
          {row.volumeUl === null ? t('mask.none') : row.volumeUl.toLocaleString(intlLocale())}
        </span>
      ),
    },
    {
      /*
       * 🔴 **고칠 수 없는 레시피에서는 입력 상자를 세우지 않는다**(eric 지적 2026-09-22).
       *    확정·보관 레시피의 lot 은 거의 비어 있어서 **테두리만 있는 빈 칸**이 8줄 섰다.
       *    빈 상자는 「적어 넣으라」는 뜻으로 읽히는데 여기는 적을 수 없는 자리다 —
       *    같은 카드의 「자리 · 웰 · 용량」이 이미 글자인 것과 이제 결이 맞는다.
       * ⚠ 「작업 생성 시 입력」 표기는 그대로다 — 그건 값이 아니라 **이 칸의 성질**이라
       *   읽기/쓰기 어느 쪽에서도 같은 말이다.
       * 🔴 **편집 가능한 초안·새 레시피에서는 입력 상자가 그대로 선다.** 아래 갈래가 그것이다.
       */
      key: 'lot',
      header: t('column.lot'),
      cell: (row) =>
        row.perBatch ? (
          <span className="muted">{t('value.perBatchInput')}</span>
        ) : readOnly ? (
          <span className="mono">{row.lot === '' ? t('mask.none') : row.lot}</span>
        ) : (
          <TextInput
            aria-label={t('column.lot')}
            onChange={(v) => patchReagent(row.channel, { lot: v })}
            {...cell(row.lot)}
            {...stainGate}
          />
        ),
    },
    /*
     * 🔴 **「작업마다」 체크박스 열을 걷었다**(2026-09-21). 두 가지가 겹쳐 있었다:
     *    ⓐ **같은 사실을 바로 옆 lot 칸이 이미 말한다**(「작업 생성 시 입력」)
     *    ⓑ **눌러도 되돌아갈 데가 없다** — 이 값은 이제 `runtime_parameters[].binds_to` 에서
     *       «읽는» 파생값이라(`recipeForm.lotBoundChannels`), 체크를 껐다 켜면 화면 상태만
     *       바뀌고 「작업 생성 입력」 탭과 **서로 다른 말**을 하게 된다. 죽은 버튼이다.
     * ⚠ 되살리려면 `runtime_parameters` 를 고치는 화면이 먼저다. 체크박스 하나로 만들지 마라.
     */
  ]

  return (
    <Card title={t('row.reagents')}>
      <DataTable
        caption={t('row.reagents')}
        columns={withRecipeTablePriority('reagents', reagentColumns)}
        expandedKey={openChannel}
        onToggleExpand={(key: string) =>
          setOpenChannel((current) => (current === key ? null : key))
        }
        revealHidden
        density="compact"
        emptyLabel={t('state.empty.generic')}
        rowKey={(row) => row.channel}
        rows={state.reagents}
      />

      {edit.disabled ? null : (
        <div className="btnrow btnrow--end">
          <Button
            size="pill"
            {...(canAddChannel
              ? { disabled: false as const }
              : { disabled: true as const, disabledReason: t('reason.reagentChannelsFull') })}
            onClick={() => {
              if (!canAddChannel) return
              onChange?.({
                ...state,
                reagents: [
                  ...state.reagents,
                  {
                    channel: String(state.reagents.length + 1),
                    name: '',
                    component: '',
                    labwareId: '',
                    wells: '',
                    volumeUl: null,
                    perBatch: false,
                    lot: '',
                  },
                ],
              })
            }}
          >
            {t('action.addChannel')}
          </Button>
        </div>
      )}
    </Card>
  )
}

/** 트레이 · 경로 카드 */
export function RecipeTrayPathCard({ state, edit, readOnly = false, recipe }: RecipeSectionsProps) {
  const groups = sampleGroupFacts(recipe.payload)
  const { editMode } = modesOf(state, edit, readOnly)

  return (
    <Card title={t('section.trayPath')}>
      {/*
        🔴 **`"4"` · `"2"` · `"C3"` 리터럴을 걷었다**(2026-09-21). 셋 다 표준 레시피
           한 건에서 베낀 값이라 **그 레시피에서만 맞고 다른 레시피에서 조용히 틀렸다.**
           출처는 `tray_path.sample_groups` 하나이고 세는 곳도 하나다(`sampleGroupFacts`) —
           「JSON 가져오기」 판정이 쓰던 그 함수다. 두 벌 만들지 않는다.
        ⚠ 환자당 챔버는 **묶음마다 다를 수 있다**(스키마 「개수를 고정하지 않는다」).
          그래서 대표값 한 칸으로 줄이지 않고 갈리면 `2 / 3` 으로 보인다.
      */}
      <div className="f2">
        <Field label={t('field.patientCount')}>
          <TextInput
            onChange={() => undefined}
            {...shown(groups.patients === 0 ? undefined : String(groups.patients))}
            {...editMode}
          />
        </Field>
        <Field label={t('field.chambersPerPatient')}>
          <TextInput
            onChange={() => undefined}
            {...shown(spread(groups.chambersPerPatient))}
            {...editMode}
          />
        </Field>
        <Field label={t('field.referenceWell')}>
          <TextInput
            onChange={() => undefined}
            {...shown(groups.referenceWells.join(' · '))}
            {...editMode}
          />
        </Field>
      </div>
    </Card>
  )
}

/**
 * 공통 카드 — 복구 허용 지연(`constraints.max_allowed_delay_sec`) 한 칸.
 *
 * 🔴 **「6축 속도」·「6축 가속」 칸이 없다**(2026-09-29 걷음). `recipe.schema.json` 최상위에
 *    `collaborative_6axis` 가 **없다**(`additionalProperties: false` · 코어 생성물과 정본 둘 다 ·
 *    정본 `12_레시피명세.md` RCP-VER-03 「6축 절 전체를 뺀 판」). 옛 레시피 17건이 본문에 그 절을
 *    싣고 있지만(실측 — 은퇴 전 저장분) 새로 만드는 레시피는 그 절을 가질 수 없다. 스키마에 없는 칸이다.
 * 🔴 **읽기 전용이면 값 없는 칸을 세우지 않는다**(2026-09-29). 확정·보관 레시피는 고칠 수 없으니
 *    빈 칸 `—` 은 「언젠가 채울 자리」가 아니라 그냥 빈 줄이다. 칸이 하나도 안 남으면 카드도 없다.
 *    ⚠ 작성 중(`draft`)·새 레시피에서는 입력 칸이라 그대로 선다.
 */
export function RecipeCommonCard({ state, edit, readOnly = false, recipe }: RecipeSectionsProps) {
  const payload = recipe.payload
  const { editMode } = modesOf(state, edit, readOnly)
  /* ⚠ 스키마가 `integer | null` 이다 — `== null` 로 둘 다 「없음」으로 본다 */
  const delaySec = payload.constraints?.max_allowed_delay_sec

  if (readOnly && delaySec == null) return null

  return (
    <Card title={t('section.common')}>
      <div className="f2">
        <Field label={t('field.recoveryDelay')}>
          <TextInput
            onChange={() => undefined}
            {...shown(
              delaySec == null ? undefined : `${String(delaySec / 60)} ${t('unit.minutes')}`,
            )}
            {...editMode}
          />
        </Field>
      </div>
    </Card>
  )
}
